<script>
import { ref, watch, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { debounce } from '@chatwoot/utils';
import SpellCheckerAPI from 'dashboard/api/spellChecker';

export default {
  name: 'SpellCheckerSuggestions',
  props: {
    text: {
      type: String,
      default: '',
    },
    textareaRef: {
      type: Object,
      default: null,
    },
  },
  emits: ['apply-correction'],
  setup(props, { emit }) {
    const store = useStore();
    const matches = ref([]);
    const isChecking = ref(false);
    const error = ref(null);
    const contextMenu = ref({
      show: false,
      x: 0,
      y: 0,
      match: null,
    });
    const overlayRef = ref(null);

    const currentAccount = computed(() => {
      const accountId = store.getters.getCurrentAccountId;
      return store.getters['accounts/getAccount'](accountId);
    });

    const isSpellCheckerEnabled = computed(() => {
      return currentAccount.value?.settings?.spell_checker_enabled;
    });

    const checkSpelling = debounce(async text => {
      if (!text || text.length < 3 || !isSpellCheckerEnabled.value) {
        matches.value = [];
        return;
      }

      isChecking.value = true;
      error.value = null;

      try {
        const response = await SpellCheckerAPI.check(text);
        matches.value = response.data.matches || [];
      } catch (err) {
        error.value = err.message;
        matches.value = [];
      } finally {
        isChecking.value = false;
      }
    }, 800);

    watch(
      () => props.text,
      newText => {
        if (isSpellCheckerEnabled.value) {
          checkSpelling(newText);
        }
      }
    );

    const escapeHtml = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };

    const highlightedText = computed(() => {
      if (!props.text || matches.value.length === 0) {
        return escapeHtml(props.text) || '&nbsp;';
      }

      let result = '';
      let lastIndex = 0;

      const sortedMatches = [...matches.value].sort((a, b) => a.offset - b.offset);

      sortedMatches.forEach((match, idx) => {
        result += escapeHtml(props.text.substring(lastIndex, match.offset));
        const errorWord = props.text.substring(match.offset, match.offset + match.length);
        result += `<mark class="spell-error" data-match-index="${idx}">${escapeHtml(errorWord)}</mark>`;
        lastIndex = match.offset + match.length;
      });

      result += escapeHtml(props.text.substring(lastIndex));
      return result || '&nbsp;';
    });

    const handleContextMenu = (event) => {
      const target = event.target;
      if (target.classList.contains('spell-error')) {
        event.preventDefault();
        event.stopPropagation();
        const matchIndex = parseInt(target.dataset.matchIndex, 10);
        const sortedMatches = [...matches.value].sort((a, b) => a.offset - b.offset);
        const match = sortedMatches[matchIndex];

        if (match) {
          // Calcula posição para mostrar acima do clique
          const menuHeight = 250; // altura estimada do menu
          let yPos = event.clientY - menuHeight;

          // Se ficar fora da tela, mostra abaixo
          if (yPos < 10) {
            yPos = event.clientY + 10;
          }

          contextMenu.value = {
            show: true,
            x: event.clientX,
            y: yPos,
            match: match,
          };
        }
      }
    };

    const hideContextMenu = () => {
      contextMenu.value.show = false;
    };

    const getErrorWord = () => {
      const match = contextMenu.value.match;
      if (!match) return '';
      return props.text.substring(match.offset, match.offset + match.length);
    };

    const applyCorrection = (replacement) => {
      const match = contextMenu.value.match;
      if (!match) return;

      const correctedText =
        props.text.substring(0, match.offset) +
        replacement +
        props.text.substring(match.offset + match.length);

      emit('apply-correction', correctedText);
      hideContextMenu();

      nextTick(() => {
        checkSpelling(correctedText);
      });
    };

    const ignoreError = () => {
      const match = contextMenu.value.match;
      if (!match) return;

      matches.value = matches.value.filter(m => m !== match);
      hideContextMenu();
    };

    // Sync scroll between textarea and overlay
    const syncScroll = () => {
      if (overlayRef.value && props.textareaRef) {
        const textarea = props.textareaRef.$el || props.textareaRef;
        if (textarea) {
          overlayRef.value.scrollTop = textarea.scrollTop;
          overlayRef.value.scrollLeft = textarea.scrollLeft;
        }
      }
    };

    onMounted(() => {
      if (props.textareaRef) {
        const textarea = props.textareaRef.$el || props.textareaRef;
        if (textarea) {
          textarea.addEventListener('scroll', syncScroll);
        }
      }
    });

    onUnmounted(() => {
      if (props.textareaRef) {
        const textarea = props.textareaRef.$el || props.textareaRef;
        if (textarea) {
          textarea.removeEventListener('scroll', syncScroll);
        }
      }
    });

    return {
      matches,
      isChecking,
      error,
      isSpellCheckerEnabled,
      highlightedText,
      contextMenu,
      overlayRef,
      handleContextMenu,
      hideContextMenu,
      getErrorWord,
      applyCorrection,
      ignoreError,
    };
  },
};
</script>

<template>
  <div
    v-if="isSpellCheckerEnabled"
    ref="overlayRef"
    class="spell-overlay"
    @contextmenu="handleContextMenu"
    v-html="highlightedText"
  />

  <Teleport to="body">
    <div v-if="contextMenu.show" class="spell-menu-backdrop" @click="hideContextMenu">
      <div
        class="spell-context-menu"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
        @click.stop
      >
        <div class="menu-header">
          <span class="error-word">{{ getErrorWord() }}</span>
          <span class="error-message">{{ contextMenu.match?.message }}</span>
        </div>
        <div class="menu-divider" />
        <div class="menu-suggestions">
          <button
            v-for="replacement in contextMenu.match?.replacements?.slice(0, 5)"
            :key="replacement"
            class="suggestion-btn"
            @click="applyCorrection(replacement)"
          >
            {{ replacement }}
          </button>
          <div v-if="!contextMenu.match?.replacements?.length" class="no-suggestions">
            {{ $t('SPELL_CHECKER.DISMISS') }}
          </div>
        </div>
        <div class="menu-divider" />
        <button class="ignore-btn" @click="ignoreError">
          {{ $t('SPELL_CHECKER.IGNORE') }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.spell-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1rem 0 0 0;
  pointer-events: none;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow: hidden;
  color: transparent;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  z-index: 1;

  :deep(.spell-error) {
    color: transparent;
    background: transparent;
    text-decoration: underline wavy #ef4444;
    text-decoration-skip-ink: none;
    text-underline-offset: 2px;
    pointer-events: auto;
    cursor: context-menu;
    border-radius: 2px;
  }
}

.spell-menu-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2147483647;
  background: transparent;
}

.spell-context-menu {
  position: fixed;
  min-width: 220px;
  max-width: 320px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
  overflow: hidden;

  .dark & {
    background: #1e293b;
    border-color: #334155;
  }
}

.menu-header {
  padding: 10px 12px;
  background: var(--s-50);

  .dark & {
    background: var(--s-900);
  }

  .error-word {
    display: block;
    font-weight: 600;
    font-size: 14px;
    color: #ef4444;
    text-decoration: line-through;
    margin-bottom: 4px;
  }

  .error-message {
    display: block;
    font-size: 12px;
    color: var(--s-600);
    line-height: 1.4;

    .dark & {
      color: var(--s-400);
    }
  }
}

.menu-divider {
  height: 1px;
  background: var(--s-200);

  .dark & {
    background: var(--s-700);
  }
}

.menu-suggestions {
  max-height: 180px;
  overflow-y: auto;
  padding: 4px 0;
}

.suggestion-btn {
  display: block;
  width: 100%;
  padding: 8px 12px;
  text-align: left;
  font-size: 14px;
  color: var(--s-800);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: var(--w-500);
    color: white;
  }

  .dark & {
    color: var(--s-100);

    &:hover {
      background: var(--w-600);
    }
  }
}

.no-suggestions {
  padding: 8px 12px;
  font-size: 13px;
  color: var(--s-500);
  text-align: center;
}

.ignore-btn {
  display: block;
  width: 100%;
  padding: 8px 12px;
  text-align: left;
  font-size: 13px;
  color: var(--s-500);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--s-100);
  }

  .dark & {
    color: var(--s-400);

    &:hover {
      background: var(--s-700);
    }
  }
}
</style>
